<style scoped>
ion-tab-bar {
  --background: #000000;
  padding: 12px 24px;
  border-top: 1px solid var(--ion-color-gray2);
}

ion-icon {
  width: 36px;
}

.icon {
  fill: #b6b6b6;
}

.icon-selected {
  fill: #222;
  background: #ffffff;
  padding: 1px 8px;
  border-radius: 16px;
}

ion-tab-button {
  --color: #b6b6b6;
  --color-selected: #ffffff;
  position: relative;
  overflow: visible;
  font-size: 14px;
}

ion-tab-button ion-icon,
ion-tab-button ion-label {
  transition: color 0.3s ease;
  color: var(--color);
}

ion-tab-button.tab-selected ion-icon,
ion-tab-button.tab-selected ion-label {
  color: var(--color-selected);
  font-weight: 500;
}

.error-indicator {
  position: absolute;
  top: 4px;
  right: 20px;
  width: 8px;
  height: 8px;
  background-color: var(--ion-color-danger);
  border-radius: 50%;
}

.transaction-badge {
  position: absolute;
  top: 0px;
  right: 20px;
  background-color: var(--ion-color-danger);
  border-radius: 50px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
</style>
<template>
  <ion-page>
    <ion-tabs>
      <ion-router-outlet :key="routerKey" />
      <ion-tab-bar slot="bottom">
        <ion-tab-button
          tab="balance"
          href="/tabs/balance"
          @click="selectedTab = 'balance'"
          data-testid="budget-tab"
        >
          <ion-icon
            :class="getIconClass('balance').value"
            :src="moneyIcon"
          />
          <ion-label>Budget</ion-label>
        </ion-tab-button>

        <ion-tab-button
          tab="transactions"
          href="/tabs/transactions"
          @click="selectedTab = 'transactions'"
          data-testid="transactions-tab"
        >
          <ion-icon
            :class="getIconClass('transactions').value"
            :src="receiptIcon"
          />
          <ion-label>Transactions</ion-label>
          <div
            v-if="uncategorizedCount > 0"
            class="transaction-badge"
          >
            {{ uncategorizedCount }}
          </div>
        </ion-tab-button>

        <ion-tab-button
          tab="accounts"
          href="/tabs/accounts"
          @click="selectedTab = 'accounts'"
          data-testid="accounts-tab"
        >
          <ion-icon
            :class="getIconClass('accounts').value"
            :src="walletIcon"
          />
          <ion-label>Accounts</ion-label>
          <div
            v-if="hasAccountErrors"
            class="error-indicator"
          />
        </ion-tab-button>
      </ion-tab-bar>
    </ion-tabs>
  </ion-page>
</template>

<script lang="ts" setup>
import {
  IonTabBar,
  IonTabButton,
  IonTabs,
  IonLabel,
  IonIcon,
  IonPage,
  IonRouterOutlet,
} from '@ionic/vue'
import { listOutline, cashOutline, walletOutline } from 'ionicons/icons'
import moneyIcon from '@/assets/img/money-icon.svg'
import receiptIcon from '@/assets/img/receipt-icon.svg'
import walletIcon from '@/assets/img/wallet-icon.svg'
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'

import { useAuthStore } from '@/stores/auth'
import { usePlaidItemsStore } from '@/stores/plaidItem'
import { useTransactionsStore } from '@/stores/transaction'
import { getCurrentYearAndMonth } from '@/utils'

const authStore = useAuthStore()
const itemsStore = usePlaidItemsStore()
const transactionsStore = useTransactionsStore()
const routerKey = computed(() => authStore.user?.cognitoId || 'guest')

const selectedTab = ref('balance')
const route = useRoute()

const hasAccountErrors = computed(() => {
  return itemsStore.items.some((item) => item.error !== null)
})

const uncategorizedCount = computed(() => {
  const currentYearMonth = getCurrentYearAndMonth()
  return transactionsStore.getUncategorizedTransactionsCount(currentYearMonth)
})

const getIconClass = (tabName: string) => {
  return computed(() =>
    selectedTab.value === tabName ? 'icon-selected' : 'icon',
  )
}

const updateSelectedTab = () => {
  if (route.path.startsWith('/tabs/accounts')) {
    selectedTab.value = 'accounts'
  } else if (route.path.startsWith('/tabs/transactions')) {
    selectedTab.value = 'transactions'
  } else {
    selectedTab.value = 'balance'
  }
}

onMounted(updateSelectedTab)
watch(() => route.path, updateSelectedTab)
</script>
